export interface IBundleSuggestionRun {
    id: string;
    status: string;
    error?: string;
    generatedDate: string;
    truncated?: boolean;
    // The run's stats complete synchronously; naming/pricing/rationale is then filled in by an LLM
    // pass in the background. 'pending' while that pass is still running, 'skipped' when it never ran.
    enrichmentStatus?: string;
    enrichmentError?: string;
    // Display names of the CRM/PSA folios the purchase history behind this run came from.
    sourceNames?: string[];
    // The mining admission thresholds actually in force for this run (some scale with order count). Undefined
    // on runs mined before this was tracked. The main UI does not render this; it exists for diagnostics.
    miningThresholds?: IBundleSuggestionMiningThresholds;
    suggestions: IBundleSuggestion[];
}

export interface IBundleSuggestionMiningThresholds {
    minCoOrders: number;
    ubiquityCutoffOrders: number;
    companionMinSharePercent: number;
    companionMinCoOrders: number;
    contextMinSharePercent: number;
    liftThreshold: number;
    jaccardThreshold: number;
    maxCompanions: number;
    maxContextItems: number;
}

export interface IBundleSuggestion {
    id: string;
    name: string;
    description?: string;
    rationale?: string;
    suggestedMarkup?: number;
    markupType?: string;
    markupBasis?: string;
    estimatedCost?: number;
    estimatedPrice?: number;
    status: string;
    acceptedBundleId?: string;
    evidence?: IBundleSuggestionEvidence;
    items: IBundleSuggestionItem[];
    // Single-select "pick one" groups (e.g. "Pick your laptop"). Group member items are also present in
    // items above (role 'groupOption', groupId set) rather than broken out here - this just describes how
    // to group and label them, and which member is selected by default.
    groups?: IBundleSuggestionGroup[];
    // Near-miss items: frequently on the same orders but not statistically strong enough to join the
    // bundle on their own. Shown for reviewer context; the LLM may promote them to optional companions.
    contextItems?: IBundleSuggestionItem[];
    // Diagnostic record of every item that rode along on this bundle's supporting orders at mining time,
    // admitted or not. Not rendered by the main UI; exists for the Partner Portal's mining breakdown.
    consideredItems?: IBundleSuggestionConsideredItem[];
    // The item (by itemKey) whose product image represents the bundle in the card/panel, when imageUrl
    // itself isn't set directly.
    keyItemKey?: string;
    imageUrl?: string;
    // Populated once the suggestion is dismissed. reason is one of DISMISS_REASON_LABELS' keys (or
    // undefined for runs dismissed before this was tracked); detail is freeform text.
    dismissReason?: string;
    dismissDetail?: string;
    // 'user' (dismissed via the dialog) | 'carriedForward' | 'existingBundle'. The latter two are
    // automatic suppressions written at generate time when a re-mined suggestion matches one that was
    // previously dismissed or already accepted into a bundle.
    dismissSource?: string;
    dismissedBy?: string;
    dismissedDate?: string;
}

// A single-select "pick one" choice within a suggestion (e.g. "Pick your laptop"). itemKeys names the
// member items (each appears in the suggestion's items with role 'groupOption' and this group's id);
// defaultItemKey is the member pre-selected in the panel.
export interface IBundleSuggestionGroup {
    id: string;
    name: string;
    itemKeys: string[];
    defaultItemKey: string;
}

// Reason codes and their user-facing labels for dismissing a bundle suggestion. 'other' pairs with a
// required freeform detail field in the dismiss dialog.
export const DISMISS_REASON_LABELS: Record<string, string> = {
    notRelated: "These products don't belong together",
    productsNotSold: "We don't sell one or more of these products",
    pricing: "The pricing or markup is off",
    duplicateBundle: "We already have a bundle like this",
    notRelevant: "Not relevant to us right now",
    other: "Other",
};

// Body for the dismiss mutation's optional reason capture.
export interface IDismissBundleSuggestionRequest {
    reason?: string;
    detail?: string;
}

export interface IBundleSuggestionConsideredItem {
    itemKey: string;
    name?: string;
    coOrderCount: number;
    sharePercent: number;
    ubiquitous: boolean;
    // 'companion' | 'context' | 'dropped'.
    outcome: string;
}

export interface IBundleSuggestionEvidence {
    lift?: number;
    coOrderCount?: number;
    supportPercent?: number;
    jaccard?: number;
    sampleOrderNames?: string[];
    // Deep-linkable sample opportunities. Preferred over sampleOrderNames when present; falls back to
    // sampleOrderNames (plain text) when this isn't populated, and to plain text per-entry when a
    // given order is missing folioId/id.
    sampleOrders?: IBundleSuggestionSampleOrder[];
}

export interface IBundleSuggestionSampleOrder {
    id: string;
    name?: string;
    folioId?: string;
}

export interface IBundleSuggestionItem {
    itemKey: string;
    manufacturer?: string;
    manufacturerPartNumber?: string;
    name?: string;
    // 'required' (default), 'optional', 'promoted', or 'groupOption'. 'groupOption' members belong to a
    // group in the suggestion's groups list (groupId set below) and are rendered in the panel as radio
    // choices rather than checkbox rows.
    role: string;
    // 'required' (default) or 'optional'. Role can additionally be 'promoted' for context items the LLM
    // moved into the bundle as its anchor; the panel checks those by default and tags them Optional.
    optionality?: string;
    orderCount?: number;
    averageUnitPrice?: number;
    averageUnitCost?: number;
    marginPercent?: number;
    quantity?: number;
    resolvedProductId?: string;
    resolvedProductCatalog?: string;
    resolvedProductName?: string;
    // Context and companion items: share (percent) of the bundle's evidence orders this item appears on.
    // Undefined on core items.
    coOrderSharePercent?: number;
    // Null/undefined means unknown (don't show a stock badge either way).
    resolvedProductInStock?: boolean;
    // Set when role is 'groupOption': the id of the IBundleSuggestionGroup this item is a member of.
    groupId?: string;
}

// Body for the accept mutation's optional bundle-configuration override. When items is present it is
// authoritative: include=false or omission excludes the item (context item keys may be included too).
export interface IAcceptBundleSuggestionRequest {
    useItemMarkup?: boolean;
    bundleMarkup?: number;
    items?: IAcceptBundleSuggestionItemRequest[];
    // Undefined means true. true: each group goes onto the created bundle as a selectable ProductGroup
    // (the server builds that from the group's members). false: only the selected member of each group
    // is added to the bundle, like a regular item.
    useGroupedOptionality?: boolean;
    groups?: IAcceptBundleSuggestionGroupRequest[];
    // Mirrors the product-level storefront Always Available setting, at the bundle level. Only sent when
    // true; omitted (not false) when the panel's toggle is off.
    storefrontAlwaysAvailable?: boolean;
    // Optional floor for the bundle's storefront price. Omitted when the panel's field is left blank.
    minimumPrice?: number;
}

export interface IAcceptBundleSuggestionItemRequest {
    itemKey: string;
    include: boolean;
    quantity?: number;
    markup?: number;
}

export interface IAcceptBundleSuggestionGroupRequest {
    groupId: string;
    selectedItemKey?: string;
}
